import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";
import { subscribeNewsletter, waLink, INSTAGRAM_URL, FACEBOOK_URL } from "../lib/api";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { metaTrack } from "../analytics/metaPixel";
import { toast } from "sonner";

export const Footer = () => {
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Please enter a valid email");
    await subscribeNewsletter(email);
    metaTrack("Lead", { content_name: "Newsletter subscription" });
    toast.success("Welcome to the Ilkal family");
    setEmail("");
  };

  return (
    <footer className="bg-surface border-t border-gold/30" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="col-span-2 lg:col-span-1">
          <p className="font-heading text-2xl text-maroon">R V ILKAL SAREES</p>
          <p className="text-sm text-charcoal/60 mt-4 leading-relaxed">
            Authentic Ilkal sarees rooted in tradition. Handwoven on pit looms in Ilkal, Karnataka, and delivered to your door across India.
          </p>
          <form onSubmit={subscribe} className="mt-6 flex" data-testid="newsletter-form">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              data-testid="newsletter-input"
              className="flex-1 min-w-0 bg-ivory border border-gold/40 px-4 py-3 text-sm outline-none focus:border-gold"
            />
            <button type="submit" data-testid="newsletter-submit-btn" className="bg-maroon text-ivory text-xs tracking-[0.15em] uppercase px-5 hover:bg-maroon-dark transition-colors">
              Join
            </button>
          </form>
        </div>

        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold">Shop</p>
          <ul className="mt-5 space-y-3 text-sm text-charcoal/70">
            <li><Link to="/shop" data-testid="footer-shop-all" className="hover:text-maroon transition-colors">All Sarees</Link></li>
            <li><Link to="/shop?badge=new" data-testid="footer-shop-new" className="hover:text-maroon transition-colors">New Arrivals</Link></li>
            <li><Link to="/shop?badge=bestseller" data-testid="footer-shop-best" className="hover:text-maroon transition-colors">Best Sellers</Link></li>
            <li><Link to="/shop?category=patteda-anchu" data-testid="footer-shop-wedding" className="hover:text-maroon transition-colors">Patteda Anchu Sarees</Link></li>
          </ul>
        </div>

        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold">Help</p>
          <ul className="mt-5 space-y-3 text-sm text-charcoal/70">
            <li><Link to="/contact" data-testid="footer-help-contact" className="hover:text-maroon transition-colors">Contact Us</Link></li>
            <li><Link to="/contact" data-testid="footer-help-shipping" className="hover:text-maroon transition-colors">Shipping</Link></li>
            <li><Link to="/contact" data-testid="footer-help-returns" className="hover:text-maroon transition-colors">Returns</Link></li>
            <li><Link to="/contact" data-testid="footer-help-faqs" className="hover:text-maroon transition-colors">FAQs</Link></li>
            <li>
              <a href={waLink("Namaste! I would like to track my order.")} target="_blank" rel="noreferrer" data-testid="footer-help-track" className="hover:text-maroon transition-colors">
                Track Order
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold">Connect</p>
          <ul className="mt-5 space-y-3 text-sm text-charcoal/70">
            <li>
              <a href={waLink("Namaste! I have a question about your sarees.")} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="inline-flex items-center gap-2 hover:text-maroon transition-colors">
                <WhatsAppIcon className="w-4 h-4" /> WhatsApp
              </a>
            </li>
            <li>
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="inline-flex items-center gap-2 hover:text-maroon transition-colors">
                <Instagram className="w-4 h-4" strokeWidth={1.5} /> Instagram
              </a>
            </li>
            <li>
              <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" data-testid="footer-facebook" className="inline-flex items-center gap-2 hover:text-maroon transition-colors">
                <Facebook className="w-4 h-4" strokeWidth={1.5} /> Facebook
              </a>
            </li>
          </ul>
          <p className="mt-6 text-sm text-charcoal/60" data-testid="footer-contact-hint">
            Full address, phone & email on our{" "}
            <Link to="/contact" className="text-gold-dark hover:text-maroon underline underline-offset-4 transition-colors">Contact page</Link>
          </p>
        </div>
      </div>

      <div className="border-t border-gold/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs tracking-wide text-charcoal/50 text-center" data-testid="footer-copyright">
            © 2026 R V ILKAL SAREES. All Rights Reserved.
          </p>
          <Link to="/admin/login" data-testid="footer-owner-login" className="text-xs tracking-wide text-charcoal/40 hover:text-maroon transition-colors">
            Owner Login
          </Link>
        </div>
      </div>
    </footer>
  );
};
